import { useEffect, useState } from "react";
import { MixboardAPI } from "@/lib/mixboard/api";
import { useMixboard } from "@/state/useMixboard";

interface TemplateMeta {
  id: string;
  name?: string;
  preview_url?: string;
}

const STUB_TEMPLATES: TemplateMeta[] = [
  { id: "stub-tour", name: "Tour Promo" },
  { id: "stub-release", name: "Release Teaser" },
  { id: "stub-merch", name: "Merch Feature" },
  { id: "stub-recap", name: "Show Recap" },
];

export function TemplatePicker() {
  const [templates, setTemplates] = useState<TemplateMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const setLayers = useMixboard((s) => s.setLayers);
  const setSession = useMixboard((s) => s.setSession);
  const setStyle = useMixboard((s) => s.setStyle);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    MixboardAPI.listTemplates()
      .then((tpls) => {
        if (cancelled) return;
        setTemplates(tpls ?? []);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("[TemplatePicker] Falling back to stub templates", err);
        setTemplates(STUB_TEMPLATES);
        setError("Using stub templates while the Mixboard API is offline.");
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLoad = (templateId: string) => {
    MixboardAPI.loadTemplate(templateId)
      .then((tmpl) => {
        setLayers(tmpl.layers ?? []);
        setStyle(tmpl.style_controls ?? {});
        setSession((prev: any) => ({ ...prev, style_controls: tmpl.style_controls }));
      })
      .catch((err) => {
        console.error("[TemplatePicker] Unable to load template", err);
        setError("Failed to load template; try another option.");
      });
  };

  if (loading) {
    return (
      <div className="space-y-2 text-xs text-muted-foreground">
        <p>Loading templates…</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error ? <p className="text-xs text-primary-foreground dark:text-amber-200">{error}</p> : null}
      <div className="grid grid-cols-2 gap-4">
        {templates.map((tpl) => (
          <button
            key={tpl.id}
            type="button"
            className="space-y-2 rounded border border-border/70 bg-background/40 p-2 text-left transition hover:border-border/40"
            onClick={() => handleLoad(tpl.id)}
          >
            {tpl.preview_url ? (
              <img src={tpl.preview_url} alt={tpl.name ?? tpl.id} className="rounded shadow" />
            ) : (
              <div className="h-20 rounded bg-muted/80" />
            )}
            <div className="text-sm font-semibold text-foreground">{tpl.name ?? tpl.id}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
